/**
 * PromptComposer — THE prompt surface for every AI entry point in Mora that is
 * not the Home chat.
 *
 * This is not a new component. It is `MediaStudioCustomPromptInput` /
 * `InlinePromptComposer` — the one composer in the app that was already being
 * reused rather than forked — promoted out of `create/v2/sections/media-studio/`
 * and given the variant axes its call sites were hand-patching with `className`
 * overrides. Both old names remain as re-exports, so no existing call site
 * churned. Rule 33: a wrong-for-context primitive gets a VARIANT, never a fork.
 *
 * Spec lineage (unchanged from its original home):
 *   - public/variant-a.html — Klaviyo-style send affordance with up-arrow
 *   - User UAT 2026-04-27 screenshot 2 — pinned-bottom-of-right-rail
 *     textarea with "Enter to send · Shift+Enter for newline" hint
 *   - DESIGN-PATTERNS.md §AI-Temporary Contract — committed-action surface,
 *     so the wrapper is `border-border/60 bg-card`, NOT skill-purple
 *
 * Controlled component — `value` + `onValueChange` are owned by the parent so an
 * AI suggestion block, a quick-action chip, or a starter can prefill the draft.
 *
 * 🔴 WHAT THIS PRIMITIVE MUST NEVER GROW. No `+` attachment menu, no mode pills,
 * no model/effort menu, no skills palette, no slash highlighting, no @-mentions,
 * no rich-text editor. Those are what make the Home composer
 * (`src/components/create/v2/composer/*`, ~1,820 lines) a DIFFERENT OBJECT, and
 * Home is deliberately not a variant of this. A request to add any of them here
 * is the signal that the caller actually wanted Home: the answer is
 * `buildAgentUrl()` and a link, not a prop. The prop surface is pinned by
 * `__tests__/prompt-composer.props.guard.test.ts` so that boundary is enforced
 * by CI rather than by this comment.
 */
import { ArrowUpIcon } from '@phosphor-icons/react';
import { type ReactNode, forwardRef, useCallback, useImperativeHandle, useRef } from 'react';

import { VoiceInput } from '@/components/create/v2/components/VoiceInput';
import { Button } from '@/components/ui/button';
import { CircleIconButton } from '@/components/ui/circle-icon-button';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import type { ArtifactKind } from '@/types/artifact-kind';
import { logger } from '@/utils/logger';

/**
 * The composer's visual register. Every value here was MEASURED off a live
 * surface that was already producing it by hand — none is invented.
 *
 *   panel — the inspector/workbench default. Unchanged from this component's
 *           original single form.
 *   page  — a page-level composer. Byte-identical to the Home composer's
 *           resting surface (`CreativeChat.tsx:1051-1053`).
 *   quiet — nested inside a card that already draws its own surface. Measured
 *           off `AskMoraComposer`, whose own comment explained WHY it was
 *           quieter: at page weight "an idle input read as focused" inside the
 *           Insights health card (founder review 2026-07-24).
 */
/**
 * The submit control's WEIGHT is part of the tone, and it was not until
 * 2026-08-27.
 *
 * `bg-foreground text-background` — a solid near-black (light) / near-white
 * (dark) filled circle — was hardcoded at all three submit branches, so a
 * composer declared `quiet` still shipped the page-weight send button. Measured
 * on `/insights` at 390x844 light: that circle was the highest-contrast object
 * in the whole control band, dominating the fold, inside the one composer on
 * the page that exists to be secondary. The founder's complaint about the ask
 * control was that it was visually overweight; capping its WIDTH answered half
 * of that and left the other half on the same axis.
 *
 * `panel` and `page` keep the exact string they had, so nothing outside the
 * quiet tone moves.
 */
const SUBMIT_STRONG = 'bg-foreground text-background hover:bg-foreground/85';
const SUBMIT_QUIET = 'bg-muted text-foreground/80 hover:bg-muted/70 hover:text-foreground';

const TONE = {
  panel: {
    surface: 'rounded-3xl border border-border/60 bg-card',
    row: 'p-2 pl-3.5',
    field: 'text-body-chat leading-normal placeholder:text-muted-foreground/70',
    submit: SUBMIT_STRONG,
  },
  page: {
    surface: 'rounded-3xl border border-border/80 bg-card shadow-composer-chat',
    row: 'p-2 pl-3.5',
    field: 'text-body-chat leading-normal placeholder:text-muted-foreground/70',
    submit: SUBMIT_STRONG,
  },
  quiet: {
    surface:
      'rounded-3xl border border-border/50 bg-background/40 transition-colors focus-within:border-ring/60',
    row: 'py-2 pl-4 pr-2',
    submit: SUBMIT_QUIET,
    // `md:text-caption` restates the token on purpose, and it is the ONLY place
    // in this file that does. `textareaVariants` emits `text-base md:text-sm`;
    // tailwind-merge cannot drop a `md:` variant when it is handed a
    // base-variant class, so a bare `text-caption` renders 12px on mobile and
    // silently 14px from `md` up — the same trap
    // `__tests__/control-height-override.guard.test.ts` documents for heights.
    field: 'text-caption md:text-caption leading-normal placeholder:text-muted-foreground/60',
  },
} as const;

export type PromptComposerTone = keyof typeof TONE;

/** A labelled submit — `Generate`, not a chat send. Carries its in-flight copy. */
export interface PromptComposerSubmitLabel {
  label: string;
  operatingLabel?: string;
}

interface PromptComposerBaseProps {
  kind: ArtifactKind;
  isOperating: boolean;
  /** Controlled draft value — owned by the parent so suggestions can fill it. */
  value: string;
  onValueChange: (value: string) => void;
  /** Returns when the operation settles. The caller wraps in op.run for the lock. */
  onSubmit: (prompt: string) => Promise<void> | void;
  /** Contextual starters / parameter controls rendered on the composer surface. */
  quickActions?: ReactNode;
  /**
   * Where `quickActions` sits. `above` is a bordered rail over the input (the
   * inspector's edit chips); `below` shares a row with the submit control (a
   * parameter row whose values change the artifact — `/studio`).
   */
  controlsPlacement?: 'above' | 'below';
  tone?: PromptComposerTone;
  /**
   * `single` caps the field at one line via the `Textarea` primitive's own
   * `maxRows`, rather than swapping in a bare `<input>` — so Enter-to-submit,
   * Shift+Enter and the draft-safety behaviour below stay identical.
   *
   * `roomy` opens at ~3 lines (64px / 80px from `md`) and grows to 192px. For
   * a composer that spans the page measure: a one-line field at 1200px reads as
   * an empty bar (measured on `/studio`, 2026-08-27); a three-line field at the
   * same width reads as a place to write.
   */
  lines?: 'auto' | 'single' | 'roomy';
  /**
   * Clear the draft once `onSubmit` resolves. ON by default (a chat send).
   * OFF where the prompt is a re-runnable parameter rather than a message —
   * `/studio` generates variants from the same sentence twice in a row, and
   * clearing it there would delete work the user still needs.
   */
  clearOnSubmit?: boolean;
  /** Overrides the kind-derived placeholder. For reuses outside media editing. */
  placeholder?: string;
  /**
   * Names this composer for assistive tech — the group and the field both.
   * Omit on media-editing surfaces to keep their established labels.
   */
  label?: string;
  className?: string;
}

/**
 * `voice` requires `onTranscript`, and the union enforces it at compile time —
 * a mic that records into nothing is the repo's silent-no-op bug class.
 */
type PromptComposerSubmitProps =
  | { submit?: 'arrow' | PromptComposerSubmitLabel; onTranscript?: never }
  | { submit: 'voice'; onTranscript: (text: string) => void };

export type PromptComposerProps = PromptComposerBaseProps & PromptComposerSubmitProps;

export interface PromptComposerHandle {
  /** Focuses the textarea + moves the caret to end. Called after a chip prefill. */
  focusAndCaretEnd: () => void;
}

const PLACEHOLDER_BY_KIND: Record<ArtifactKind, string> = {
  image: 'Tell Mora what to change…',
  video: 'Ask Mora to edit this video…',
  post: 'Describe a change to this caption…',
};

/**
 * FIXED 2026-08-27 — this is the pass the previous comment here was waiting for,
 * and it was taken deliberately, with a stated intent, not as a refactor side
 * effect.
 *
 * THE INTENT, in one line: **the composer SHELL is the input; the field owns only
 * the text.** A composer is one control, so it draws one surface — the rounded,
 * bordered group below — and the textarea inside it is transparent in both
 * themes.
 *
 * What was wrong. `bg-transparent` alone was a SILENT NO-OP in dark, twice over:
 *   1. `src/index.css` forces `background-color: hsl(var(--input)) !important` on
 *      every `<textarea>` not marked `data-slot="input-group-control"`.
 *   2. `textareaVariants` declares `dark:bg-input` on its own base class, and
 *      tailwind-merge cannot drop a `dark:` variant when handed a base-variant
 *      class — so `bg-transparent` never displaced it.
 * BOTH have to be answered or the slab silently returns; that is why
 * `dark:bg-transparent` and the `data-slot` below travel together, and why
 * `__tests__/prompt-composer.surface.guard.test.ts` asserts both.
 *
 * Measured on `/studio` at 1440x900, dark: page `rgb(16,16,18)` → composer shell
 * `rgb(24,24,27)` → field `rgb(39,39,42)`. A +15/255 raised slab inside its own
 * card, against the design basis' 4–10 ceiling for container-vs-ground (P10),
 * with a border the same colour as its own fill (P32). Founder, twice: _"it
 * should be an empty input. Why does it look like a different contrast where the
 * text is? That is a terrible design there."_
 *
 * THE EVIDENCE THAT IT WAS NEVER A DECISION: Home's composer — the app's other
 * page-level composer, and the shape this one's `page` tone is byte-copied from —
 * measures shell `rgb(24,24,27)` and field `rgba(0,0,0,0)` in the same theme.
 * Home escapes the rule only because its editable region is a
 * `div[contenteditable]`, which the CSS tag selector structurally cannot match.
 * The slab was an accident of tag choice, and Home is the proof of what the
 * field should look like.
 *
 * Blast radius, all five verified live in both themes on 2026-08-27: `/studio`,
 * Insights' Ask (`tone="quiet"`), the media-studio dialog, the inspector bodies,
 * and the workbench prompt bar. Light mode never matched the CSS rule, so no
 * light surface moved.
 */
const FIELD_BASE =
  'resize-none overflow-y-auto border-0 bg-transparent dark:bg-transparent shadow-none p-0';
const FIELD_AUTO_HEIGHT = 'min-h-composer-icon-mobile md:min-h-composer-icon-desktop max-h-36 py-1';
/**
 * `min-h-0` is load-bearing, not tidying. `textareaVariants`' default size
 * carries `min-h-16` (64px); the `auto` string above displaces it, `single` had
 * nothing to displace it with, and the one-line Ask field rendered 82px tall.
 */
const FIELD_SINGLE_HEIGHT = 'min-h-0';
const FIELD_ROOMY_HEIGHT = 'min-h-16 md:min-h-20 max-h-48 py-1';
const FIELD_FOCUS = 'focus-visible:ring-0 focus-visible:ring-offset-0';

export const PromptComposer = forwardRef<PromptComposerHandle, PromptComposerProps>(
  function PromptComposer(
    {
      kind,
      isOperating,
      value,
      onValueChange,
      onSubmit,
      quickActions,
      controlsPlacement = 'above',
      tone = 'panel',
      lines = 'auto',
      clearOnSubmit = true,
      submit: submitAffordance = 'arrow',
      onTranscript,
      placeholder,
      label,
      className,
    },
    ref,
  ) {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const canSubmit = value.trim().length > 0 && !isOperating;
    const single = lines === 'single';
    const below = controlsPlacement === 'below';

    useImperativeHandle(ref, () => ({
      focusAndCaretEnd: () => {
        const ta = textareaRef.current;
        if (!ta) return;
        ta.focus();
        const pos = ta.value.length;
        ta.setSelectionRange(pos, pos);
      },
    }));

    // Auto-grow lives in the `Textarea` primitive now (`autoGrow`, on by
    // default), which still honours the `max-h-36` cap declared below. This
    // component was the original home of that idiom and three other surfaces
    // had copied it; keeping a local copy alongside the primitive would put two
    // height-setters on one node, the repo's recurring silent-no-op shape.

    const submit = useCallback(async () => {
      if (!canSubmit) return;
      const trimmed = value.trim();
      // Clear the draft only AFTER onSubmit resolves (Cycle 3 ISSUE-007 — a
      // network failure used to permanently lose the user's prompt). But the
      // textarea is NOT disabled during in-flight (`isOperating` only disables
      // the send button), so users can keep typing while the AI generates.
      // Only clear if the textarea still contains the value we just sent —
      // otherwise the user has typed something new and we'd nuke their draft.
      // Cycle 7 /review caught this regression.
      const sentValue = value;
      try {
        await onSubmit(trimmed);
        if (clearOnSubmit && textareaRef.current?.value === sentValue) {
          onValueChange('');
        }
      } catch (err) {
        logger.error('PromptComposer: onSubmit failed', { kind, err });
      }
    }, [canSubmit, value, onValueChange, onSubmit, kind, clearOnSubmit]);

    const submitControl =
      typeof submitAffordance === 'object' ? (
        <Button
          type="button"
          size="sm"
          disabled={!canSubmit}
          onClick={() => void submit()}
          className="shrink-0"
        >
          {isOperating
            ? (submitAffordance.operatingLabel ?? submitAffordance.label)
            : submitAffordance.label}
        </Button>
      ) : submitAffordance === 'voice' && !canSubmit && onTranscript ? (
        <VoiceInput className={TONE[tone].submit} onTranscript={onTranscript} />
      ) : submitAffordance === 'voice' ? (
        <CircleIconButton
          size="composer"
          ariaLabel={label ? 'Send' : 'Send custom edit'}
          className={cn(TONE[tone].submit, 'shrink-0')}
          onClick={() => void submit()}
        >
          <ArrowUpIcon weight="bold" />
        </CircleIconButton>
      ) : (
        <Button
          type="button"
          variant="flat"
          size="composer-icon"
          aria-label={label ? 'Send' : 'Send custom edit'}
          onClick={() => void submit()}
          disabled={!canSubmit}
          className={cn(
            TONE[tone].submit,
            'transition-colors shrink-0',
            tone !== 'quiet' && 'shadow-sm',
          )}
        >
          {isOperating ? (
            <Spinner size="sm" className="text-background" />
          ) : (
            <ArrowUpIcon weight="bold" />
          )}
        </Button>
      );

    return (
      <div
        role="group"
        aria-label={label ?? 'Media editing composer'}
        className={cn('flex flex-col overflow-hidden', TONE[tone].surface, className)}
      >
        {quickActions && !below ? (
          <div className="border-b border-border/30 px-3 py-2">{quickActions}</div>
        ) : null}
        <div
          className={cn(
            // rounded-3xl + the send-button spec below mirror the home-chat
            // composer (CreativeChat) exactly, so every composer surface reads
            // identical.
            'flex gap-1.5',
            single ? 'items-center' : 'items-end',
            TONE[tone].row,
          )}
        >
          <Textarea
            ref={textareaRef}
            data-slot="input-group-control"
            value={value}
            onChange={(e) => onValueChange(e.target.value)}
            placeholder={placeholder ?? PLACEHOLDER_BY_KIND[kind]}
            rows={1}
            maxRows={single ? 1 : undefined}
            aria-label={label ?? 'Describe a custom edit'}
            className={cn(
              FIELD_BASE,
              single
                ? FIELD_SINGLE_HEIGHT
                : lines === 'roomy'
                  ? FIELD_ROOMY_HEIGHT
                  : FIELD_AUTO_HEIGHT,
              FIELD_FOCUS,
              // This prompt is a primary action, not metadata. `text-detail`
              // made the image/video surfaces read like a disabled footnote
              // while the post caption editor used a much larger body treatment.
              TONE[tone].field,
            )}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void submit();
              }
            }}
          />
          {below ? null : submitControl}
        </div>
        {below ? (
          // `ml-auto` on the submit, not on any one quick action. This row wraps
          // by design — six parameter controls do not fit one 744px line — and
          // before this, the submit was simply the next flex item, so on wrap it
          // landed alone at the LEFT of a new line and the commit action read as
          // a stray chip. Anchored right, it reads as the row's footer whether it
          // wraps or not. Owned here rather than at the call site because the
          // caller cannot reach a control this component renders.
          <div className="flex flex-wrap items-center gap-1.5 px-3.5 pb-2">
            {quickActions}
            <div className="ml-auto flex items-center">{submitControl}</div>
          </div>
        ) : null}
      </div>
    );
  },
);
